<!--
  Accounts and invitations.

  Two things on this page are easy to get wrong and are handled deliberately:

  1. **The invite token is shown exactly once.** The service stores a SHA-256 of it, so there
     is no "show it again" — the button copies it, and the panel says plainly that closing it
     loses it. Pretending otherwise would be a lie about what the database holds.

  2. **Nothing here deletes a user.** Deactivation is the only removal. `users` is the record
     of who an `annotators` row *was*, and every box that person drew points at that row; a
     deleted user leaves a provenance chain that technically survives and practically nobody
     can read.

  The self-guards (an admin cannot demote or deactivate themselves, and the last active admin
  cannot be demoted at all) are enforced by the service. This page also disables the controls,
  so the answer arrives before the click rather than as a 403 after it.
-->
<script lang="ts">
  import { api } from '../lib/api/client';
  import { asProblem } from '../lib/api/problem';
  import type { InviteCreated, InviteOut, UserOut, UserRole } from '../lib/api/types';
  import { auth } from '../lib/stores/auth.svelte';

  const ROLES: UserRole[] = ['admin', 'annotator', 'viewer'];

  let users = $state<UserOut[]>([]);
  let invites = $state<InviteOut[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let notice = $state<string | null>(null);

  let inviteEmail = $state('');
  let inviteRole = $state<UserRole>('annotator');
  let issuing = $state(false);
  let issued = $state<InviteCreated | null>(null);
  let copied = $state(false);

  const activeAdmins = $derived(
    users.filter((user) => user.role === 'admin' && user.is_active).length,
  );

  async function refresh(): Promise<void> {
    loading = true;
    error = null;
    try {
      const [userList, inviteList] = await Promise.all([api.users(), api.invites()]);
      users = userList.users;
      invites = inviteList;
    } catch (caught) {
      error = asProblem(caught).message;
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    void refresh();
  });

  /** Would changing this account leave nobody who can administer the deployment? */
  function isLastAdmin(user: UserOut): boolean {
    return user.role === 'admin' && user.is_active && activeAdmins <= 1;
  }

  function isSelf(user: UserOut): boolean {
    return user.id === auth.user?.id;
  }

  async function patch(user: UserOut, change: Partial<UserOut>): Promise<void> {
    error = null;
    notice = null;
    try {
      const updated = await api.patchUser(user.id, {
        ...(change.role !== undefined ? { role: change.role } : {}),
        ...(change.is_active !== undefined ? { is_active: change.is_active } : {}),
      });
      users = users.map((row) => (row.id === updated.id ? updated : row));
      notice = `${updated.email} updated.`;
    } catch (caught) {
      error = asProblem(caught).message;
    }
  }

  async function issue(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (issuing || !inviteEmail.trim()) return;
    issuing = true;
    error = null;
    notice = null;
    copied = false;
    try {
      issued = await api.createInvite(inviteEmail.trim(), inviteRole);
      inviteEmail = '';
      invites = await api.invites();
    } catch (caught) {
      error = asProblem(caught).message;
    } finally {
      issuing = false;
    }
  }

  function inviteLink(created: InviteCreated): string {
    // Built from the browser's own origin rather than from anything the service returned:
    // the API is on a different host (Fly) from the SPA (CloudFront) and has no idea where
    // the frontend is served from, which is exactly why it hands back a *path*.
    const base = typeof location === 'undefined' ? '' : `${location.origin}${location.pathname}`;
    return `${base}${created.accept_path}`;
  }

  async function copyLink(created: InviteCreated): Promise<void> {
    try {
      await navigator.clipboard.writeText(inviteLink(created));
      copied = true;
    } catch {
      copied = false;
      error = 'Could not reach the clipboard. Select the link and copy it by hand.';
    }
  }

  async function revoke(invite: InviteOut): Promise<void> {
    error = null;
    try {
      await api.revokeInvite(invite.id);
      invites = await api.invites();
      notice = `The invitation for ${invite.email} was revoked.`;
    } catch (caught) {
      error = asProblem(caught).message;
    }
  }
</script>

<div class="page">
  <h1>Accounts</h1>

  {#if error}
    <p class="error" role="alert">{error}</p>
  {/if}
  {#if notice}
    <p class="notice" role="status">{notice}</p>
  {/if}

  <section class="panel">
    <h2>Invite somebody</h2>
    <form class="row" onsubmit={issue}>
      <input
        type="email"
        placeholder="name@example.org"
        bind:value={inviteEmail}
        required
        aria-label="Email to invite"
      />
      <select bind:value={inviteRole} aria-label="Role">
        {#each ROLES as role (role)}
          <option value={role}>{role}</option>
        {/each}
      </select>
      <button class="primary" type="submit" disabled={issuing || !inviteEmail.trim()}>
        {issuing ? 'Issuing…' : 'Issue a link'}
      </button>
    </form>
    <p class="muted small">
      The role is fixed by the invitation — whoever redeems it gets exactly this one, and
      cannot choose another. A <strong>viewer</strong> reads everything and writes nothing.
    </p>

    {#if issued}
      <div class="issued">
        <p><strong>Send this link to {issued.invite.email}.</strong></p>
        <code class="link">{inviteLink(issued)}</code>
        <div class="row">
          <button onclick={() => copyLink(issued!)}>
            {copied ? 'Copied' : 'Copy link'}
          </button>
          <button class="subtle" onclick={() => (issued = null)}>Done</button>
        </div>
        <p class="warn small">
          This is the only time the link is shown. The service stores a hash of it, not the
          link, so it cannot be looked up again — if it is lost, revoke this invitation and
          issue a new one.
        </p>
      </div>
    {/if}
  </section>

  <section class="panel">
    <h2>People</h2>
    {#if loading}
      <p class="muted">Loading…</p>
    {:else}
      <table>
        <thead>
          <tr>
            <th>Email</th>
            <th>Name</th>
            <th>Annotator id</th>
            <th>Role</th>
            <th>Active</th>
          </tr>
        </thead>
        <tbody>
          {#each users as user (user.id)}
            <tr class:inactive={!user.is_active}>
              <td>{user.email}{#if isSelf(user)}<span class="muted"> (you)</span>{/if}</td>
              <td>{user.full_name}</td>
              <td class="mono">{user.annotator_id ?? '—'}</td>
              <td>
                <select
                  value={user.role}
                  disabled={isSelf(user) || isLastAdmin(user)}
                  onchange={(event) =>
                    patch(user, { role: event.currentTarget.value as UserRole })}
                  aria-label={`Role for ${user.email}`}
                >
                  {#each ROLES as role (role)}
                    <option value={role}>{role}</option>
                  {/each}
                </select>
              </td>
              <td>
                <button
                  class="subtle"
                  disabled={isSelf(user) || isLastAdmin(user)}
                  onclick={() => patch(user, { is_active: !user.is_active })}
                >
                  {user.is_active ? 'Deactivate' : 'Reactivate'}
                </button>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
      <p class="muted small">
        There is no delete. Deactivating stops an account signing in and keeps the record of
        who an annotator id belonged to — without it, every box that person drew is attributed
        to a name nobody can look up. You cannot change your own role or deactivate yourself,
        and the last active administrator cannot be demoted by anyone.
      </p>
    {/if}
  </section>

  <section class="panel">
    <h2>Invitations</h2>
    {#if invites.length === 0}
      <p class="muted">None issued yet.</p>
    {:else}
      <table>
        <thead>
          <tr>
            <th>Email</th>
            <th>Role</th>
            <th>State</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {#each invites as invite (invite.id)}
            <tr>
              <td>{invite.email}</td>
              <td>{invite.role}</td>
              <td>
                {#if invite.accepted_at}
                  accepted
                {:else if invite.revoked_at}
                  revoked
                {:else if invite.is_open}
                  open
                {:else}
                  expired
                {/if}
              </td>
              <td>
                {#if invite.is_open}
                  <button class="subtle" onclick={() => revoke(invite)}>Revoke</button>
                {/if}
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
      <p class="muted small">
        Accepted, revoked and expired invitations stay listed: "who did we invite, and what
        happened to it?" is not a question a deleted row can answer.
      </p>
    {/if}
  </section>
</div>

<style>
  .page {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px;
    max-width: 960px;
  }

  h1 {
    margin: 0;
    font-size: 1.3rem;
  }

  h2 {
    margin: 0 0 8px;
    font-size: 1rem;
  }

  .panel {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .row {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }

  table {
    border-collapse: collapse;
    width: 100%;
  }

  th,
  td {
    text-align: left;
    padding: 6px 8px;
    border-bottom: 1px solid var(--rule, #3333);
    font-size: 0.9rem;
  }

  tr.inactive td {
    opacity: 0.55;
  }

  .issued {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px;
    border: 1px solid var(--warn, #b8860b);
    border-radius: 4px;
  }

  .link {
    word-break: break-all;
    font-size: 0.85rem;
  }

  .error,
  .notice {
    margin: 0;
    padding: 8px 10px;
    border-radius: 4px;
    font-size: 0.9rem;
  }

  .error {
    border: 1px solid var(--danger, #b3261e);
  }

  .notice {
    border: 1px solid var(--rule, #3333);
  }

  .warn {
    color: var(--warn, #b8860b);
  }

  .small {
    font-size: 0.82rem;
    line-height: 1.5;
  }
</style>
